import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { getCountryFlag } from '@/lib/countries'
import { formatDuration, formatInteger } from './formatters'
import { slaTier, tierText, tierBar } from './slaHealth'
import { cn } from '@/lib/utils'
import type { ResponseTimeByCountryPanel } from '@/types/analytics'

const COUNTRY_NAMES = new Intl.DisplayNames(['en'], { type: 'region' })

interface ResponseTimeByCountryCardProps {
  data: ResponseTimeByCountryPanel
  mode: 'absolute' | 'relative'
}

export function ResponseTimeByCountryCard({ data, mode }: ResponseTimeByCountryCardProps) {
  if (data.rows.length === 0) {
    return (
      <Card className="p-4">
        <CardHeader className="px-0 pb-3">
          <CardTitle className="text-base">Response time by country</CardTitle>
          <CardDescription>Median first reply per visitor country</CardDescription>
        </CardHeader>
        <CardContent className="px-0 text-sm text-muted-foreground">
          No country data for this range. Once visitors with recorded locations get replies, they'll appear here.
        </CardContent>
      </Card>
    )
  }

  const valueFor = (row: ResponseTimeByCountryPanel['rows'][number]) =>
    (mode === 'relative' ? row.relative_median : row.absolute_median) ?? 0
  const maxValue = Math.max(...data.rows.map(valueFor), 1)

  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3">
        <CardTitle className="text-base">Response time by country</CardTitle>
        <CardDescription>
          Slowest first · {mode === 'relative' ? 'within support hours' : 'wall-clock median'}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-0 space-y-2">
        {data.rows.map((row) => {
          const value = valueFor(row)
          const widthPct = Math.max(2, Math.round((value / maxValue) * 100))
          const name = safeCountryName(row.country_code)
          const flag = getCountryFlag(row.country_code)
          const tier = slaTier(value, data.sla_seconds)
          return (
            <div key={row.country_code} className="grid grid-cols-[24px_1fr_auto] items-center gap-2 text-xs">
              <span className="text-base leading-none" aria-hidden="true">
                {flag ?? <span className="font-mono text-muted-foreground text-xs">{row.country_code}</span>}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate">{name}</span>
                  <span className="text-muted-foreground tabular-nums whitespace-nowrap">· {formatInteger(row.replies)}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={cn('h-full', tierBar[tier])} style={{ width: `${widthPct}%` }} />
                </div>
              </div>
              <div className={cn('tabular-nums text-right whitespace-nowrap font-medium', tierText[tier])}>
                {formatDuration(value)}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

function safeCountryName(code: string): string {
  try {
    return COUNTRY_NAMES.of(code) ?? code
  } catch {
    return code
  }
}
